// src/pages/consultar-planeaciones.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import '../styles/formularios.css';
import '../styles/styles.css';
import logoInstituto from '../components/images/logo.png';
import logoSEP from '../components/images/SEP_Logo_2019.svg.png';
import Swal from 'sweetalert2';
import { resolveApiBaseList } from '../utils/apiBase';

const stripLeadingSlashes = (value = '') => value.replace(/^\/+/, '');

const buildAssetUrl = (assetBase, relativePath, fallbackOrigin) => {
  const normalizedBase = (assetBase || fallbackOrigin || '').replace(/\/$/, '');
  const normalizedPath = stripLeadingSlashes(relativePath);
  if (!normalizedPath) {
    return normalizedBase || fallbackOrigin || '';
  }
  if (!normalizedBase) {
    return `/${normalizedPath}`;
  }
  return `${normalizedBase}/${normalizedPath}`;
};

function getIdDocenteFromLocalStorage() {
  const claves = ['IdDocente', 'idDocente', 'docenteId', 'IdUsuarioDocente'];
  for (const k of claves) {
    const v = Number(localStorage.getItem(k));
    if (Number.isInteger(v) && v > 0) return v;
  }
  return null;
}

function filenameFromPath(p = '') {
  try {
    const parts = p.split('/');
    return parts[parts.length - 1] || p;
  } catch { return p; }
}

const ConsultarPlaneaciones = () => {
  const defaultOrigin = useMemo(() => (typeof window !== 'undefined' ? window.location.origin : ''), []);
  const apiBases = useMemo(() => resolveApiBaseList(), []);
  const [planeaciones, setPlaneaciones] = useState([]);
  const [panelActivo, setPanelActivo] = useState(null);
  const [visorPDF, setVisorPDF] = useState(false);
  const [pdfURL, setPdfURL] = useState('');

  const fetchJsonWithFallback = useCallback(async (path, options = {}) => {
    let lastError = null;

    for (const base of apiBases) {
      const normalizedBase = base.replace(/\/$/, '');
      const normalizedPath = path.startsWith('/') ? path : `/${path}`;
      const url = `${normalizedBase}${normalizedPath}`;

      try {
        const response = await fetch(url, options);
        const text = await response.text();

        if (!response.ok) {
          let message = text || `Error ${response.status}`;
          try {
            const parsed = text ? JSON.parse(text) : null;
            if (parsed && typeof parsed === 'object') {
              message = parsed.message || parsed.error || message;
            }
          } catch (parseErr) {
            // mantener mensaje original
          }
          const error = new Error(message);
          error.status = response.status;
          lastError = error;
          console.warn(`Warning: petición fallida a ${url}:`, error);
          continue;
        }

        const contentType = response.headers.get('content-type') || '';
        const trimmed = text.trim();
        if (!trimmed) {
          // Use the absolute response URL as base so assets resolve to the backend that answered
          const apiBase = response.url.replace(new RegExp(`${normalizedPath.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}$`), '');
          return { data: {}, baseUrl: apiBase };
        }

        if (contentType.includes('application/json') || trimmed.startsWith('{') || trimmed.startsWith('[')) {
          const parsed = JSON.parse(trimmed);
          const apiBase = response.url.replace(new RegExp(`${normalizedPath.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}$`), '');
          return { data: parsed, baseUrl: apiBase };
        }

        try {
          const parsed = JSON.parse(trimmed);
          const apiBase = response.url.replace(new RegExp(`${normalizedPath.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}$`), '');
          return { data: parsed, baseUrl: apiBase };
        } catch (parseErr) {
          lastError = new Error(`Respuesta inválida desde ${url}`);
          console.warn(`Warning: contenido no JSON desde ${url} (content-type: ${contentType || 'desconocido'})`);
        }
      } catch (error) {
        lastError = error;
        console.warn(`Warning: error de red al solicitar ${url}:`, error);
      }
    }

    throw lastError || new Error('No se pudo contactar al servidor.');
  }, [apiBases]);

  useEffect(() => {
    const cargarPlaneaciones = async () => {
      const idDoc = getIdDocenteFromLocalStorage();
      const idUsuario = Number(localStorage.getItem('idUsuario'));

      let path = null;
      if (Number.isInteger(idDoc) && idDoc > 0) {
        path = `/planeaciones/docente/${idDoc}`;
      } else if (Number.isInteger(idUsuario) && idUsuario > 0) {
        path = `/planeaciones/usuario/${idUsuario}`;
      } else {
        Swal.fire('Falta ID', 'No se encontró IdDocente ni idUsuario en el navegador. Inicia sesión nuevamente.', 'warning');
        return;
      }

      try {
        const { data, baseUrl } = await fetchJsonWithFallback(path);
          const baseForAssets = baseUrl || defaultOrigin;

        const ordenadas = Array.isArray(data)
          ? [...data].sort((a, b) => Number(a.IdPlaneaciones) - Number(b.IdPlaneaciones))
          : [];

        const mapped = ordenadas.map((p) => {
          const rel = stripLeadingSlashes(p.PlaneacionURL || '');
          return {
            id: p.IdPlaneaciones,
            tema: p.Tema || '',
            descripcion: p.Descripcion || '',
            observaciones: p.Observaciones || '',
            status: p.Status || '',
            rutaArchivo: buildAssetUrl(baseForAssets, rel, defaultOrigin),
            archivo: filenameFromPath(rel)
          };
        });

        setPlaneaciones(mapped);
      } catch (e) {
        console.error('Error al cargar planeaciones:', e);
        Swal.fire('Error', 'No se pudieron cargar las planeaciones.', 'error');
      }
    };

    cargarPlaneaciones();
  }, [defaultOrigin, fetchJsonWithFallback]);

  const togglePanel = (id) => {
    setPanelActivo(panelActivo === id ? null : id);
  };

  const abrirVistaPrevia = (ruta) => {
    setPdfURL(ruta);
    setVisorPDF(true);
  };

  const cerrarPDF = () => {
    setPdfURL('');
    setVisorPDF(false);
  };

  return (
    <div className="container py-4 px-4">
      {/* Encabezado */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <img src={logoInstituto} alt="Logo Instituto" className="logo" />
        <div className="flex-grow-1 text-center">
          <h4 className="mb-0">Instituto Silviano Carrillo</h4>
          <small className="text-muted fs-5">Consultar Planeaciones</small>
        </div>
        <img src={logoSEP} alt="Logo SEP" className="logo" />
      </div>

      {/* Tarjetas colapsables */}
      <div className="row g-4">
        {planeaciones.length === 0 && (
          <div className="col-12">
            <div className="alert alert-info mb-0">No hay planeaciones registradas.</div>
          </div>
        )}

        {planeaciones.map((plan) => (
          <div className="col-md-6" key={plan.id}>
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <h5 className="card-title">Planeación #{plan.id}</h5>
                <p className="card-text"><strong>Tema:</strong> {plan.tema}</p>
                <p className="card-text"><strong>Descripción:</strong> {plan.descripcion || <em>(Sin descripción)</em>}</p>

                <button
                  
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => togglePanel(plan.id)}
                >
                  {panelActivo === plan.id ? 'Ocultar detalles' : 'Mostrar detalles'}
                </button>

                {panelActivo === plan.id && (
                  <div className="mt-3" onClick={(e) => e.stopPropagation()}>
                    {/* CONTENEDOR CON ALTURA FIJA + SCROLL: solo se expande esta zona */}
                    <div
                      style={{
                        maxHeight: 360,   // ajusta si quieres un poco más/menos
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        paddingRight: 6
                      }}
                    >
                      <label className="form-label mt-2">Tema a Impartir</label>
                      <input
                        type="text"
                        className="form-control mb-2"
                        value={plan.tema}
                        disabled
                        readOnly
                      />

                      <label className="form-label">Descripción</label>
                      <textarea
                        className="form-control mb-3"
                        rows="3"
                        value={plan.descripcion}
                        disabled
                        readOnly
                      />

                      <label className="form-label">Archivos adjuntos</label>
                      <div className="table-responsive mb-3">
                        <table className="table table-bordered table-sm align-middle text-center">
                          <thead className="table-light">
                            <tr>
                              <th style={{width: 60}}>#</th>
                              <th>Nombre</th>
                              <th style={{width: 120}}>Acción</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td>1</td>
                              <td className="text-truncate" title={plan.archivo}
                                  style={{maxWidth: 280, whiteSpace: 'nowrap', overflow: 'hidden'}}>
                                {plan.archivo}
                              </td>
                              <td>
                                <button
                                  
                                  className="btn btn-sm btn-primary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    abrirVistaPrevia(plan.rutaArchivo);
                                  }}
                                >
                                  Vista previa
                                </button>
                                {/* ⬇️ Quitado botón Descargar */}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      <label className="form-label">Observaciones</label>
                      <textarea
                        className="form-control mb-2"
                        rows="2"
                        value={plan.observaciones || ''}
                        readOnly
                      />
                      <small className="text-muted">
                        * Las observaciones solo pueden ser modificadas por la Directora.
                      </small>
                    </div>
                    {/* FIN del contenedor con scroll */}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Visor PDF emergente */}
      {visorPDF && (
        <div
          style={{
            display: 'flex',
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.6)',
            zIndex: 9999,
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <div
            style={{
              position: 'relative',
              width: '80vw',
              maxWidth: '1000px',
              height: '90vh',
              background: 'white',
              borderRadius: '10px',
              overflow: 'hidden',
              boxShadow: '0 0 30px rgba(0,0,0,0.4)'
            }}
          >
            <div style={{ background: '#343a40', color: 'white', padding: '10px 20px', fontWeight: 'bold' }}>
              Vista previa del archivo
              <button onClick={cerrarPDF} style={{ float: 'right' }} className="btn btn-danger btn-sm">✖</button>
            </div>

            <iframe
              src={pdfURL}
              style={{ width: '100%', height: 'calc(100% - 45px)', border: 'none' }}
              title="Visor de archivo"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsultarPlaneaciones;
